import { Button, HorizontalDivider, Notification, Typography } from '@smartgift/frontend-toolkit';
import uniq from 'lodash/uniq';
import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { CardContent } from '@hero/atoms';
import { AvailableCharitiesModal, GiftCollectionSizeModal } from '@hero/components/modals';
import {
  AUTOMATION_STATUSES,
  NON_EDITABLE_AUTOMATION_STATUS,
  PAYMENT_METHOD_TYPES,
  PRODUCT_TYPES
} from '@hero/constants';
import { useGetCharity } from '@hero/hooks';
import { ProductVerticalList, VerticalListHeader } from '@hero/molecules';
import { CharityVerticalListHeader, EditableProductsModal } from '@hero/organisms';
import { useGetAutomation, useGetAutomationProducts, useUpdateAutomation } from '@hero/services';
import { useNotification } from '@hero/store/hooks';
import { hasAnyAvailableProducts } from '@hero/utils';

import * as S from './style';

function AutomationReviewGiftsAccordion({ refetchAutomation }) {
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const { code } = useParams();
  const { data: automation, isFetching: isFetchingAutomation } = useGetAutomation({ code });
  const { mutateAsync: onUpdate } = useUpdateAutomation({
    options: {
      onSuccess: () => {
        if (refetchAutomation) refetchAutomation();
      }
    }
  });
  const { charityEnabled, isCharityExist } = useGetCharity();
  const { data: allProductsResponse, isFetching: isFetchingProducts } = useGetAutomationProducts(
    code,
    {
      onSuccess: data => {
        if (!hasAnyAvailableProducts(data)) {
          showNotification({
            title: (
              <Trans
                key="noAvailableProducts"
                i18nKey="noAvailableProducts"
                values={{ feature: t('automation').toLocaleLowerCase() }}
              />
            ),
            message: (
              <Trans
                key="noAvailableProductsDesc"
                i18nKey="noAvailableProductsDesc"
                values={{ feature: t('automation').toLocaleLowerCase() }}
              />
            ),
            primaryButtonProps: { children: t('editGiftCollection') },
            onSubmit: () => {
              setShowProductModal(true);
            }
          });
        }
      }
    }
  );

  const allProducts = allProductsResponse || [];

  const [showSizeWarnOpen, setShowSizeWarnOpen] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCharitiesModal, setShowCharitiesModal] = useState(false);

  const isDetailItemEditable = useMemo(
    () => !NON_EDITABLE_AUTOMATION_STATUS.includes(automation?.status),
    [automation]
  );

  const products = useMemo(() => {
    return allProducts.filter(p => p?.meta?.productType !== PRODUCT_TYPES.CHARITY);
  }, [allProducts]);

  const charities = useMemo(() => {
    return allProducts.filter(p => p?.meta?.productType === PRODUCT_TYPES.CHARITY);
  }, [allProducts]);

  const unavailableProducts = uniq(automation?.products)?.filter(
    p => !products?.map(product => product.merchantProductId).includes(p.merchantProductId)
  );

  const isLiveFeature = automation?.status === AUTOMATION_STATUSES.active;

  const selectedProducts = useMemo(() => {
    return allProducts?.filter(p => p?.meta?.productType !== PRODUCT_TYPES.CHARITY);
  }, [allProducts]);

  const selectedCharities = useMemo(() => {
    return allProducts?.filter(p => p?.meta?.productType === PRODUCT_TYPES.CHARITY);
  }, [allProducts]);

  const updateProducts = (
    products,
    successMessage = t('giftsSuccessfullyAdded', { featureType: 'automation' })
  ) => {
    onUpdate({
      params: { code },
      body: {
        products: uniq([...products, ...selectedCharities])
      },
      successMessage
    });
  };

  const updateCharities = charities =>
    onUpdate({
      params: { code },
      body: {
        products: uniq([...selectedProducts, ...charities])
      },
      successMessage: t('charitySuccessfullyAdded', { featureType: 'automation' })
    });

  const onRemoveItem = merchantProductId => {
    const product =
      selectedProducts.find(item => item.merchantProductId === merchantProductId) ||
      selectedCharities.find(item => item.merchantProductId === merchantProductId);
    const successMessage = t('successfullyRemovedMessage', { name: product?.name });

    onUpdate({
      params: { code },
      body: {
        products: [...selectedProducts, ...selectedCharities].filter(
          item => item.merchantProductId !== merchantProductId
        )
      },
      successMessage
    });
  };

  const openProductModal = () => {
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
  };

  const sizeModalHandler = () => {
    setShowSizeWarnOpen(false);
    openProductModal();
  };

  return (
    <S.GiftsWrapper>
      {!selectedProducts?.length && (
        <Notification
          status="warning"
          message={
            <Trans
              i18nKey="badgeIncludeAtLeastOneTagToActivate"
              values={{
                featureType: t('automation').toLocaleLowerCase(),
                contactType: t('giftItem').toLocaleLowerCase()
              }}
              components={{ b: <b /> }}
            />
          }
        />
      )}
      <CardContent>
        <VerticalListHeader
          products={products}
          updateProducts={updateProducts}
          setShowProductModal={setShowProductModal}
          isDetailItemEditable={isDetailItemEditable}
          isLiveFeature={isLiveFeature}
          rearrangeEffectiveType={t('futureGiftSends')}
          isFetching={isFetchingProducts || isFetchingAutomation}
        />

        {automation?.products.filter(p => p.meta?.productType !== PRODUCT_TYPES.CHARITY)?.length >
          0 && (
          <ProductVerticalList
            featureName={t('automation').toLocaleLowerCase()}
            products={{ docs: products }}
            selectedProducts={selectedProducts}
            creationProducts={automation?.products.filter(
              p => p.meta?.productType !== PRODUCT_TYPES.CHARITY
            )}
            isFetching={isFetchingProducts}
            onRemoveProduct={onRemoveItem}
            unavailableProducts={unavailableProducts}
            updateProducts={updateProducts}
          />
        )}

        {charityEnabled && isCharityExist && (
          <>
            <HorizontalDivider $margin="24px 0" />
            <CharityVerticalListHeader
              collectionSize={charities?.length}
              setShowCharitiesModal={setShowCharitiesModal}
              isDetailItemEditable={isDetailItemEditable}
              isFetching={isFetchingProducts || isFetchingAutomation}
            />
            {charities?.length ? (
              <ProductVerticalList
                featureName={t('automation').toLocaleLowerCase()}
                products={{ docs: charities }}
                creationProducts={automation?.products.filter(
                  p => p.meta?.productType === PRODUCT_TYPES.CHARITY
                )}
                selectedProducts={selectedCharities}
                isFetching={isFetchingProducts}
                onRemoveProduct={onRemoveItem}
                isProductsRemovable={isDetailItemEditable}
                updateProducts={updateCharities}
                featureStatus={automation?.status}
              />
            ) : (
              <S.EmptySelectedCharityPlaceholderWrapper>
                <Typography variant="body2">{t('addCharityPlaceholderText')}</Typography>
                <Button
                  onClick={() => setShowCharitiesModal(true)}
                  disabled={automation?.status === AUTOMATION_STATUSES.deactivated}
                >
                  {t('addACharitableGiving')}
                </Button>
              </S.EmptySelectedCharityPlaceholderWrapper>
            )}
          </>
        )}

        <HorizontalDivider $margin="24px 0" />

        <S.Description variant="body2SemiBold" color="neutral" emphasis="500">
          {t('verticalListSubTitle')}
        </S.Description>

        {automation && showProductModal && (
          <EditableProductsModal
            isModalOpen={showProductModal}
            onRequestClose={closeProductModal}
            selectedProducts={selectedProducts}
            updateProducts={updateProducts}
            featureType="automation"
            isLiveFeature={isLiveFeature}
            isDraft={automation.status === AUTOMATION_STATUSES.draft}
            isCCPayment={automation?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard}
          />
        )}
        {automation && showCharitiesModal && (
          <AvailableCharitiesModal
            isOpen={showCharitiesModal}
            selectedCharities={selectedCharities}
            setSelectedCharities={updateCharities}
            setIsOpen={setShowCharitiesModal}
            saveWithoutProducts={true}
          />
        )}
      </CardContent>
      <GiftCollectionSizeModal
        isOpen={showSizeWarnOpen}
        setIsOpen={setShowSizeWarnOpen}
        primaryButtonProps={{
          children: t('editGiftCollection'),
          onClick: sizeModalHandler
        }}
      />
    </S.GiftsWrapper>
  );
}

AutomationReviewGiftsAccordion.propTypes = {
  refetchAutomation: PropTypes.func
};

export { AutomationReviewGiftsAccordion };
